import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  DropdownProps,
  DropdownRef,
} from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import useCallbackRef from 'hooks/useCallbackRef';
import ShareIcon from 'icons/Share.icon';
import classNames from 'classnames';
import { Button, Dropdown } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import { ReactComponent as LinkedIn } from 'icons/linkedin.svg';
import MailopenIcon from 'icons/Mailopen.icon';
import { ReactComponent as Facebook } from 'icons/facebook.svg';
import LinkIcon from 'icons/Link.icon';
import {
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
} from 'react-share';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import useTrackShareInitiated from 'hooks/useTrackShareInitiated';
import { LISTING_SHARE_MODE } from 'models/MixpanelEvents';

import './ShareButton.styles.scss';

type ShareButtonProps = {
  url: string;
  listingId: string;
  listingName: string;
  className?: string;
} & Partial<Omit<DropdownProps, 'ref'>>;

const ShareButton: ForwardRefRenderFunction<DropdownRef, ShareButtonProps> = (
  props,
  ref,
) => {
  const { t } = useTranslation();
  const {
    className,
    url,
    listingId,
    listingName,
    trigger = (
      <Button variant="link" icon={ShareIcon}>
        {t('share')}
      </Button>
    ),
    ...rest
  } = props;
  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>();

  useImperativeHandle(ref, () => ({ ...dropdown }), [dropdown]);

  const classes = classNames('share-button-portal', className);

  const copyToClipboard = useCopyToClipboard();

  const trackShareInitiated = useTrackShareInitiated({
    listingId,
    listingName,
  });

  return (
    <Dropdown
      ref={dropdownRef}
      renderAsPortal
      portalClassName={classes}
      trigger={trigger}
      {...rest}
    >
      <div>
        <Button
          icon={LinkIcon}
          onClick={(e) => {
            copyToClipboard(e, url);
            trackShareInitiated(LISTING_SHARE_MODE.CLIPBOARD);
            dropdown.setIsOpen(false);
          }}
        />
        <p>{t('copyLink')}</p>
      </div>
      <div>
        <EmailShareButton
          url={url}
          onClick={(_, link) => {
            trackShareInitiated(LISTING_SHARE_MODE.EMAIL);
            window.location.href = link;
          }}
        >
          <MailopenIcon />
        </EmailShareButton>
        <p>{t('mail')}</p>
      </div>
      <div>
        <LinkedinShareButton
          url={url}
          onClick={() => trackShareInitiated(LISTING_SHARE_MODE.LINKEDIN)}
        >
          <LinkedIn />
        </LinkedinShareButton>
        <p>{t('linkedin')}</p>
      </div>
      <div>
        <FacebookShareButton
          url={url}
          onClick={() => trackShareInitiated(LISTING_SHARE_MODE.FACEBOOK)}
        >
          <Facebook />
        </FacebookShareButton>
        <p>{t('facebook')}</p>
      </div>
    </Dropdown>
  );
};

export default forwardRef(ShareButton);
